import { map } from 'rxjs/operators';
import { Injectable } from "@angular/core";
import * as selfScheduling from "../interfaces/Iself.scheduling";
import { LogManager } from "../../../common/log/log.manager";
import { LogCat } from "../../../common/log/log.categories";
import { HttpObservable, HttpWrapper } from "../../../common/http-helpers/http.wrapper";
import {
	ILocationSelectionRequest,
	IOrgInstructionRequest,
	ISPSInstructionRequest,
	IRenewReservationRequest,
	IReservation,
	IPortalScheduleChangeMessage,
	ICheckCanNewOrderAndScheduleRequest,
	RulesValidationOverride
} from "../interfaces/Iself.scheduling";
import { ILocationSelectionResponse } from "../interfaces/Iself.scheduling";
import { ISearchStudyRequest } from "../interfaces/Iself.scheduling";

/**
 ****************************************************************
 * Proxy service class for self scheduling.
 *
 * Created by Shai Zelman on 27/02/2020.
 ****************************************************************
 */
@Injectable({
	providedIn: "root"
})
export class SelfSchedulingProxy {
	constructor(private httpWrapper: HttpWrapper) { }

	/**
	 * Gets the list of orders and appointments for the patient.
	 * @returns An HttpObservable with the list of appointments.
	 */
	public getOrdersAndAppointments(): HttpObservable<number[]> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Get orders and appointments for the patient.");

		const date = new Date();
		return this.httpWrapper.get("PatientPortalScheduling", "GetPatientOrdersAndAppointments", [
			{ pName: "navigatorUserAgent", pVal: navigator.userAgent },
			{ pName: "navigatorLanguage", pVal: navigator.language },
			{ pName: "date", pVal: date },
			{ pName: "dateToLocaleString", pVal: date.toLocaleString() },
			{ pName: "dateToUTCString", pVal: date.toUTCString() },
			{ pName: "dateGetTimezoneOffset", pVal: date.getTimezoneOffset() }
		]).pipe(map(data => this.httpWrapper.parseDates(data, new RegExp(".*"))));
	}


	public getAllSuggestionsForDay(
		sessionId: string,
		studies: selfScheduling.ISelectedStudy[],
		orderKey: number,
		requestedProcedureId: number,
		startDate: Date,
		endDate: Date,
		orgStructureKeys: number[],
		daysTimeOptions: selfScheduling.IDaysTimeOptions,
		searchesPerDay: selfScheduling.ISearchesPerDay,
		opCodeKey: number,
		selectedResources: { [resourceRole: number]: number[] } = null
	): HttpObservable<selfScheduling.ISearchResult[][]> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Get available time slots for selected studies.", sessionId, studies, orderKey,
			startDate, endDate, orgStructureKeys, daysTimeOptions, searchesPerDay, opCodeKey, selectedResources);

		return this.httpWrapper.post("PatientPortalScheduling", "GetAllSuggestionsForDay", {
			sessionId: sessionId,
			selectedStudies: studies,
			orderKey: orderKey,
			requestedProcedureId: requestedProcedureId,
			startDate: startDate,
			endDate: endDate,
			orgStructureKeys: orgStructureKeys,
			daysTimeOptions: daysTimeOptions,
			searchesPerDay: searchesPerDay,
			opCodeKey: opCodeKey,
			selectedResources: selectedResources
		}).pipe(map(data => this.httpWrapper.parseDates(data, new RegExp(".*"))));
	}

	public findAvailableDaysInRange(
		sessionId: string,
		studies: selfScheduling.ISelectedStudy[],
		orderKey: number,
		requestedProcedureId: number,
		startDate: Date,
		endDate: Date,
		orgStructureKeys: number[],
		daysTimeOptions: selfScheduling.IDaysTimeOptions,
		searchesPerDay: selfScheduling.ISearchesPerDay,
		opCodeKey: number,
		selectedResources: { [resourceRole: number]: number[] } = null
	): HttpObservable<selfScheduling.ISearchResult[][]> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Get available time slots for selected studies.", sessionId, studies, orderKey,
			startDate, endDate, orgStructureKeys, daysTimeOptions, searchesPerDay, opCodeKey, selectedResources);

		return this.httpWrapper.post("PatientPortalScheduling", "FindAvailableDaysInRange", {
			sessionId: sessionId,
			selectedStudies: studies,
			orderKey: orderKey,
			requestedProcedureId: requestedProcedureId,
			startDate: startDate,
			endDate: endDate,
			orgStructureKeys: orgStructureKeys,
			daysTimeOptions: daysTimeOptions,
			searchesPerDay: searchesPerDay,
			opCodeKey: opCodeKey,
			selectedResources: selectedResources
		}).pipe(map(data => this.httpWrapper.parseDates(data, new RegExp(".*"))));
	}

	/**
	 * Gets available time slots for the selected studies with daily result count settings.
	 * @param sessionId
	 * @param studies
	 * @param orderKey
	 * @param requestedProcedureId
	 * @param startDate
	 * @param endDate
	 * @param orgStructureKeys
	 * @param daysTimeOptions
	 * @param opCodeKey
	 * @param selectedResources
	 * @returns An HttpObservable containing the data.
	 */
	public getRecommendedTimeSlots(
		sessionId: string,
		studies: selfScheduling.ISelectedStudy[],
		orderKey: number,
		requestedProcedureId: number,
		startDate: Date,
		endDate: Date,
		orgStructureKeys: number[],
		daysTimeOptions: selfScheduling.IDaysTimeOptions,
		opCodeKey: number,
		selectedResources: { [resourceRole: number]: number[] } = null
	): HttpObservable<selfScheduling.ISearchResult[][]> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Get available time slots for selected studies.", sessionId, studies, orderKey,
			startDate, endDate, orgStructureKeys, daysTimeOptions, opCodeKey, selectedResources);

		return this.httpWrapper.post("PatientPortalScheduling", "GetRecommendedTimeSlots", {
			sessionId: sessionId,
			selectedStudies: studies,
			orderKey: orderKey,
			requestedProcedureId: requestedProcedureId,
			startDate: startDate,
			endDate: endDate,
			orgStructureKeys: orgStructureKeys,
			daysTimeOptions: daysTimeOptions,
			opCodeKey: opCodeKey,
			selectedResources: selectedResources
		}).pipe(map(data => this.httpWrapper.parseDates(data, new RegExp(".*"))));
	}

	/**
	 * Gets a list of Modality Type Groups.
	 * @returns An HttpObservable containing an array of Modality Type Groups.
	 */
	public getModalityTypeGroups(): HttpObservable<any[]> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Get Modality Type Groups.");

		return this.httpWrapper.get("PatientPortalScheduling", "GetModalityTypeGroups");
	}

	/**
	 * Gets a list of Holidays for a given month.
	 * @returns An HttpObservable containing an array dates.
	 */
	public getHolidaysForMonth(month: Date): HttpObservable<Date[]> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Get Dates list representing holidays for a given month.", month);

		let regExp = new RegExp(".*");
		return this.httpWrapper.get("PatientPortalScheduling", "GetHolidaysForMonth", [
			{ pName: "month", pVal: month }
		]).pipe(map(data => this.httpWrapper.parseDates(data, regExp)));
	}

	public getSpsCodesBySearchTerm(request: ISearchStudyRequest): HttpObservable<any[]> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Get SPS codes by search term.", request.searchTerm);

		return this.httpWrapper.put("PatientPortalScheduling", "GetSpsCodesBySearchTerm", request);
	}

	public getPortalPatient(): HttpObservable<{ patient: any }> {
		return this.httpWrapper.get("Patient", "GetPatientDetailByPatientKey");
	}

	public getPortalOrder(orderKey): HttpObservable<{ patient: any }> {
		return this.httpWrapper.get("Order", "GetPatientDetailByOrderKey", [
			{ pName: "orderKey", pVal: orderKey }
		]);
	}


	public getCommonExams(languageKey: number): HttpObservable<any[]> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Get common exams for language key.", languageKey);

		return this.httpWrapper.get("PatientPortalScheduling", "GetCommonExams", [{ pName: "languageKey", pVal: languageKey }]);
	}

	public getAllExams(languageKey: number): HttpObservable<any[]> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Get all exams for language key.", languageKey);

		return this.httpWrapper.get("PatientPortalScheduling", "GetAllExams", [{ pName: "languageKey", pVal: languageKey }]);
	}

	public checkCanNewOrderAndSchedule(request: ICheckCanNewOrderAndScheduleRequest): HttpObservable<boolean> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Check canNewOrderAndSchedule for sps code key:", request.spsCodeKey);

		return this.httpWrapper.put("PatientPortalScheduling", "CheckCanNewOrderAndSchedule", request);
	}

	public getLocationSelectionData(request: ILocationSelectionRequest): HttpObservable<ILocationSelectionResponse> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Gets a valid location selection tree.");

		return this.httpWrapper.put("PatientPortalScheduling", "GetLocationSelectionData", request);
	}

	public getCancellationReasons(langKey: number): HttpObservable<selfScheduling.ICancellationReason[]> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Gets a list of cancellation reasons.");

		return this.httpWrapper.get("PatientPortalScheduling", "GetCancellationReasons", { pName: "langKey", pVal: langKey });
	}

	public cancelStudy(request: selfScheduling.IChangeStatusRequest): HttpObservable<any> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Cancel study.", request);

		return this.httpWrapper.put("PatientPortalScheduling", "PutCancelStudy", request);
	}

	public scheduleStudies(scheduleData: selfScheduling.IScheduleData, override: RulesValidationOverride, opCodeKey: number, orderKey: number = -1): HttpObservable<{ [spsId: number]: selfScheduling.IScheduleResponseDetails }> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Schedule studies.", scheduleData.selectedScheduling);

		return this.httpWrapper.put("PatientPortalScheduling", "ScheduleStudies", scheduleData, [
			{ pName: "overrideRuleValidation", pVal: override },
			{ pName: "opCodeKey", pVal: opCodeKey },
			{ pName: "orderKey", pVal: orderKey }
		]);
	}

	public rescheduleStudies(scheduleData: selfScheduling.ISearchResult[], override: RulesValidationOverride, opCodeKey: number, orderKey: number = -1): HttpObservable<{ [spsId: number]: selfScheduling.IScheduleResponseDetails }> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Update schedule.", scheduleData);

		return this.httpWrapper.put("PatientPortalScheduling", "RescheduleStudies", scheduleData, [
			{ pName: "overrideRuleValidation", pVal: override },
			{ pName: "opCodeKey", pVal: opCodeKey },
			{ pName: "orderKey", pVal: orderKey }
		]);
	}

	public scheduleStudiesWithModel(model, override: RulesValidationOverride): HttpObservable<any> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Update schedule with model.", model);

		return this.httpWrapper.put("PatientPortalScheduling", "ScheduleStudiesWithModel", {
			model: model,
			returnModel: true,
			RulesValidationOverride: override
		});
	}

	public updateOrderWithModel(model, override: RulesValidationOverride, opCodeKey: number): HttpObservable<any> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Update order with model.", model);

		return this.httpWrapper.put("PatientPortalScheduling", "UpdateOrderWithModel", { model: model }, [
			{ pName: "overrideRuleValidation", pVal: override },
			{ pName: "opCodeKey", pVal: opCodeKey }
		]);
	}

	public updateOrderWithoutModel(orderData: selfScheduling.IOrderUpdateData, override: RulesValidationOverride, opCodeKey: number, orderKey: number): HttpObservable<any> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Update order without model.", {
			orderData, 
			override,
			opCodeKey,
			orderKey
		});

		return this.httpWrapper.put("PatientPortalScheduling", "UpdateOrderWithoutModel", orderData, [
			{ pName: "overrideRuleValidation", pVal: override },
			{ pName: "opCodeKey", pVal: opCodeKey },
			{ pName: "orderKey", pVal: orderKey }
		]);
	}

	public SaveScanDocsWithOverride(orderData: selfScheduling.IOrderUpdateData, orderKey: number = -1): HttpObservable<any> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "Save scan documents for order update.", {
			orderData,
			orderKey
		});

		return this.httpWrapper.put("PatientPortalScheduling", "SaveScanDocsWithOverride", orderData.acquiredDocuments, [
			{ pName: "orderKey", pVal: orderKey }
		]);
	}

	public announceScheduleChange(data: IPortalScheduleChangeMessage): HttpObservable<any> {
		return this.httpWrapper.put("PatientPortalScheduling", "AnnounceScheduleChange", data);
	}

	public isSchedulingAllowed(model): HttpObservable<string> {
		return this.httpWrapper.post("PatientPortalScheduling", "IsSchedulingAllowed", model);
	}

	public deleteReservations(sessionId: string): HttpObservable<any> {
		return this.httpWrapper.put("PatientPortalScheduling", "DeleteReservations", sessionId);
	}

	public renewReservation(renewRequest: IRenewReservationRequest): HttpObservable<any> {
		return this.httpWrapper.put("PatientPortalScheduling", "RenewReservation", renewRequest);
	}

	public reserveTimeslots(reservation: IReservation): HttpObservable<any> {
		return this.httpWrapper.put("PatientPortalScheduling", "ReserveTimeslots", reservation);
	}

	public GetContactInfoHtml(languageKey: number): HttpObservable<string> {
		return this.httpWrapper.putAnon("PatientPortalScheduling", "GetContactInfoHtml", languageKey);
	}

	public GetOrgInstructionsHtml(request: IOrgInstructionRequest): HttpObservable<string> {
		return this.httpWrapper.put("PatientPortalScheduling", "GetOrgInstructionsHtml", request);
	}

	public GetSPSInstructionsHtml(request: ISPSInstructionRequest): HttpObservable<string> {
		return this.httpWrapper.put("PatientPortalScheduling", "GetSPSInstructionsHtml", request);
	}

	public CheckRowsExistForOpCodeKey(opCodeKey: number): HttpObservable<boolean> {
		return this.httpWrapper.put("PatientPortalScheduling", "CheckRowsExistForOpCodeKey", opCodeKey);
	}

	public GetSchedulingSearchInterval(
		orderKey: number,
		opCodeKey: number,
		selectedStudies: selfScheduling.ISelectedStudy[]): HttpObservable<Date[]> {
		LogManager.debug(LogCat.SelfScheduling.Proxy, "GetSchedulingSearchInterval", orderKey, opCodeKey, selectedStudies);

		let regExp = new RegExp(".*");
		return this.httpWrapper.get("PatientPortalScheduling", "GetSchedulingSearchIntervalFromMultipleStudies", [
			{ pName: "selectedStudies", pVal: selectedStudies },
			{ pName: "orderKey", pVal: orderKey },
			{ pName: "opCodeKey", pVal: opCodeKey },
		]).pipe(map(data => this.httpWrapper.parseDates(data, regExp)));
	}

	public logSessionError(opCodeKey: number, data: Object): HttpObservable<any> {
		return this.httpWrapper.put("PatientPortalScheduling", "LogSessionError", data, { pName: "opCodeKey", pVal: opCodeKey });
	}

	public GetAllowedDocumentTypesForPatient(): HttpObservable<{scanDocTypeKey: number, description: string}[]> {
		return this.httpWrapper.get("PatientPortalScheduling", "GetAllowedDocumentTypesForPatient");
	}
}
